import { ShipmentFormPayment } from '@prisma/client';
import { useCallback } from 'react';

import { trpc, TrpcOutputs } from '../../config/trpc';

type ShipmentForms = TrpcOutputs['getShipmentFormsByRequestId'] | undefined;

type Args = {
  quoteId?: number;
  requestId?: number;
  shipmentForms?: ShipmentForms;
};

/**
 * Persist an ITN number onto the MAWB ShipmentForm, which is where `itn_number` lives.
 *
 * The ITN is collected in two places that can be dismissed without ever opening the AWB
 * Instructions modal — the paperwork card, and the post-booking modal when "Are your AWB
 * instructions ready?" is answered "No". That modal was the field's only consumer, so on
 * those paths whatever the user typed was silently discarded on reload. It has to be written
 * here rather than merely handed forward.
 *
 * Updates the existing MAWB when there is one; otherwise creates the same minimal MAWB the
 * AWB Instructions flow would have created (`PRE_PAID`, matching `createEmptyMawbForm`) so
 * the number survives either way. A no-op when the value is blank or unchanged, so this is
 * safe to call on every blur.
 */
export const usePersistItnNumber = ({ quoteId, requestId, shipmentForms }: Args) => {
  const trpcUtils = trpc.useUtils();
  const { mutateAsync: createShipmentForm } = trpc.createShipmentForm.useMutation();
  const { mutateAsync: updateShipmentForm } = trpc.updateShipmentForm.useMutation();

  return useCallback(
    async (itnNumber?: string | null) => {
      const value = itnNumber?.trim();
      if (!value || !quoteId || !requestId) return;

      const mawb = shipmentForms?.find((form) => form.parent_shipment_form_id == null);
      if (mawb?.itn_number?.trim() === value) return;

      try {
        if (mawb) {
          await updateShipmentForm({ id: mawb.id, itn_number: value });
        } else {
          await createShipmentForm({
            quote_id: quoteId,
            request_id: requestId,
            payment: ShipmentFormPayment.PRE_PAID,
            itn_number: value,
          });
        }

        await Promise.all([
          trpcUtils.getShipmentFormsByRequestId.invalidate({ requestId, quoteId }),
          trpcUtils.getFinalQuote.invalidate({ id: quoteId }),
        ]);
      } catch {
        // Losing the ITN is the failure this exists to prevent, but it is a background
        // side-save on a field the user has already moved past — surfacing an error toast
        // here would interrupt a flow they are not blocked on. The value stays in the form
        // state and is written again by the AWB Instructions save.
      }
    },
    [createShipmentForm, quoteId, requestId, shipmentForms, trpcUtils, updateShipmentForm],
  );
};
